/** Shared UI pieces used by both MemberDashboard and ManagerDashboard. */

export function WelcomeBanner({ name, subtitle, children }) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/20 bg-[linear-gradient(145deg,#1b2857_0%,#4a1a06_60%,#f07415_100%)] px-4 py-4 text-white shadow-[0_12px_32px_rgba(27,40,87,0.20)] lg:px-5">
      <div className="absolute right-0 top-0 h-24 w-24 translate-x-1/4 -translate-y-1/4 rounded-full bg-[#f07415]/20 blur-2xl" />
      <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-orange-100/80">AWTF Dashboard</p>
          <h1 className="mt-1 text-lg font-semibold leading-tight tracking-tight lg:text-xl">
            Welcome, {name}
          </h1>
          {subtitle && <p className="text-xs text-orange-100/70 mt-0.5">{subtitle}</p>}
        </div>
        {children && <div className="flex flex-wrap gap-2 shrink-0">{children}</div>}
      </div>
    </section>
  );
}

const STAT_COLORS = {
  blue:  { shell: 'border-orange-200/60 bg-[linear-gradient(145deg,rgba(255,247,237,0.92),rgba(255,255,255,0.96))]', icon: 'bg-[#f07415] text-white' },
  green: { shell: 'border-emerald-200/60 bg-[linear-gradient(145deg,rgba(236,253,245,0.92),rgba(255,255,255,0.96))]', icon: 'bg-emerald-500 text-white' },
  amber: { shell: 'border-amber-200/60 bg-[linear-gradient(145deg,rgba(255,251,235,0.92),rgba(255,255,255,0.96))]', icon: 'bg-amber-500 text-white' },
  rose:  { shell: 'border-rose-200/60 bg-[linear-gradient(145deg,rgba(255,241,242,0.92),rgba(255,255,255,0.96))]', icon: 'bg-rose-500 text-white' },
};

const ICON_PATHS = {
  invoice:       'M6.75 4.5h10.5A1.5 1.5 0 0 1 18.75 6v12a1.5 1.5 0 0 1-1.5 1.5H6.75A1.5 1.5 0 0 1 5.25 18V6a1.5 1.5 0 0 1 1.5-1.5Zm2.25 3.75h6m-6 3h6m-6 3h3',
  paid:          'm7.5 12 3 3 6-6M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  alert:         'M12 9v4.5m0 3h.008v.008H12V16.5Zm8.25 1.5a8.25 8.25 0 1 1-16.5 0 8.25 8.25 0 0 1 16.5 0Z',
  claims:        'M7.5 4.5h9M7.5 8.25h9M6 3h12A1.5 1.5 0 0 1 19.5 4.5v15A1.5 1.5 0 0 1 18 21H6A1.5 1.5 0 0 1 4.5 19.5v-15A1.5 1.5 0 0 1 6 3Zm2.25 10.5 1.5 1.5 3.75-3.75',
  members:       'M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0ZM3.5 20a6.5 6.5 0 0 1 13 0M18 8a3 3 0 1 1 0 6M18.5 20a5.5 5.5 0 0 0-2.2-4.4',
  beneficiaries: 'M12 21s-6.75-4.35-9-9.3C1.5 7.8 4.2 4.5 8.1 4.5c1.7 0 3 .8 3.9 2 0-.1.1-.1.1-.2.9-1.1 2.2-1.8 3.8-1.8 3.9 0 6.6 3.3 5.1 7.2-2.25 4.95-9 9.3-9 9.3Z',
};

export function StatCard({ title, value, icon, color }) {
  const c = STAT_COLORS[color] || STAT_COLORS.blue;
  return (
    <div className={`rounded-2xl border p-4 shadow-[0_6px_20px_rgba(15,23,42,0.05)] ${c.shell}`}>
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${c.icon}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
            <path strokeLinecap="round" strokeLinejoin="round" d={ICON_PATHS[icon] || ICON_PATHS.paid} />
          </svg>
        </div>
        <div className="min-w-0">
          <p className="break-words text-[9px] font-semibold uppercase tracking-[0.08em] text-slate-500">{title}</p>
          <p className="mt-0.5 text-lg font-bold tracking-tight text-slate-900 leading-tight">{value}</p>
        </div>
      </div>
    </div>
  );
}

export function MiniMetric({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-white/70 bg-white/70 px-3 py-2">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <span className="rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-[0.14em] text-white">
        {value}
      </span>
    </div>
  );
}
